import { reactive, watch } from 'vue';

interface State {
  active: boolean;
}

const cache: Record<string, State> = {};
const featureStates: Map<string, { state: State; userId: string | null }> = new Map();

function getUserId(): string | null {
  try {
    const page = usePage();
    return page?.props?.user?.id ?? null;
  }
  catch {
    return null;
  }
}

function fetchFeature(name: string, state: State) {
  fetch(`/feature/${name}`)
    .then(r => r.json())
    .then((f) => {
      state.active = f.active;
    });
}

export function useFeature(name: string): State;
export function useFeature<T extends readonly string[]>(names: T): Record<T[number], State>;
export function useFeature<T extends readonly string[]>(nameOrNames: string | T): State | Record<T[number], State> {
  if (typeof nameOrNames === 'string') {
    const name = nameOrNames;
    const currentUserId = getUserId();
    const cacheKey = `${currentUserId ?? 'anonymous'}:${name}`;

    if (cache[cacheKey]) {
      return cache[cacheKey];
    }

    const state = reactive({
      active: false,
    });

    cache[cacheKey] = state;
    featureStates.set(name, { state, userId: currentUserId });

    fetchFeature(name, state);

    try {
      const page = usePage();
      watch(() => page?.props?.user?.id, (newUserId) => {
        const tracked = featureStates.get(name);
        const userId = newUserId ?? null;
        if (tracked && tracked.userId !== userId) {
          tracked.userId = userId;
          fetchFeature(name, tracked.state);
        }
      });
    }
    catch {
    }

    return state;
  }

  const names = nameOrNames as T;
  const result: Record<T[number], State> = {} as Record<T[number], State>;
  const currentUserId = getUserId();

  names.forEach((name) => {
    const cacheKey = `${currentUserId ?? 'anonymous'}:${name}`;

    if (cache[cacheKey]) {
      result[name as T[number]] = cache[cacheKey];
    }
    else {
      const state = reactive({
        active: false,
      });
      result[name as T[number]] = state;
      cache[cacheKey] = state;
      featureStates.set(name, { state, userId: currentUserId });

      fetchFeature(name, state);

      try {
        const page = usePage();
        watch(() => page?.props?.user?.id, (newUserId) => {
          const tracked = featureStates.get(name);
          const userId = newUserId ?? null;
          if (tracked && tracked.userId !== userId) {
            tracked.userId = userId;
            fetchFeature(name, tracked.state);
          }
        });
      }
      catch {
      }
    }
  });

  return result;
}
